import { isNull } from "underscore";
import BRequest from "../../../modules/BRequest";
import EdibleContainer from "./EdibleContainer";
import Form from "./Form";
import TagContainer from "./TagContainer";
import ShowcaseContainer from "./ShowcaseContainer";

export default class Showcase
{
	constructor(el)
	{
		this.el = el;
	}

	static selector = () => `.diary-day-search-edibles-showcase`;

	static initialize()
	{
		this.addEventListeners();
	}

	static addEventListeners()
	{
		document.addEventListener("click", event => {
			const selector = `${this.selector()}__show-category`;
			if (event.target.matches(`${selector}, ${selector} *`)) {
				event.preventDefault();
				const showcase = new this(event.target.closest(this.selector()));
				showcase.getForm().showCategory(showcase.getCategory());
			}
		});

		document.addEventListener("click", event => {
			const selector = `${this.selector()}__load-more-in-category`;
			if (event.target.matches(`${selector}, ${selector} *`)) {
				event.preventDefault();
				const showcase = new this(event.target.closest(this.selector()));
				showcase.loadMore();
			}
		});

		document.querySelectorAll(`dialog`).forEach(dialogEl => {
			dialogEl.addEventListener("scroll", event => {
				document.querySelectorAll(this.selector()).forEach(el => {
					new this(el).loadMoreIfApplicable();
				});
			});
		});
	}

	getSelector()
	{
		return Showcase.selector();
	}

	getForm()
	{
		return new Form(this.el.closest(Form.selector()));
	}

	getShowcaseContainer()
	{
		return new ShowcaseContainer(this.el.closest(ShowcaseContainer.selector()));
	}

	getTagContainer()
	{
		const el = this.el.querySelector(TagContainer.selector());
		if (el) {
			return new TagContainer(el);
		}

		return null;
	}

	getCategory()
	{
		return this.el.dataset.category;
	}

	getAffix()
	{
		return this.el.dataset.affix;
	}

	getPreloaderElement()
	{
		return this.el.querySelector(`${this.getSelector()}__preloader`);
	}

	showPreloader()
	{
		this.hideNoData();
		this.getPreloaderElement().hidden = false;

		return this;
	}

	hidePreloader()
	{
		this.getPreloaderElement().hidden = true;

		return this;
	}

	setPagination(pagination)
	{
		this.setTotal(pagination.total);
		this.setPage(pagination.page);
		this.setPerPage(pagination.perPage);

		const totalElement = this.el.querySelector(`${this.getSelector()}__total`);
		if (totalElement) {
			totalElement.innerHTML = pagination.total;
		}

		const showAllElement = this.el.querySelector(`${this.getSelector()}__show-all`);
		if (showAllElement) {
			showAllElement.hidden = !(pagination.total > this.getLimit());
		}

		const moreCount = pagination.total - (pagination.page * pagination.perPage);

		const moreCountElement = this.getMoreCountElement();
		if (moreCountElement) {
			moreCountElement.innerHTML = (moreCount >= 0) ? moreCount : 0;
		}

		const loadMoreElement = this.getLoadMoreElement();
		if (loadMoreElement) {
			loadMoreElement.hidden = moreCount <= 0;
		}

		return this;
	}

	setTotal(total)
	{
		this.el.dataset.total = total;

		return this;
	}

	getTotal()
	{
		return parseInt(this.el.dataset?.total);
	}

	getCount()
	{
		return this.getEdibleContainer().getEdibleCollection().getEdibles().length;
	}

	setPage(page)
	{
		this.el.dataset.page = page;

		return this;
	}

	getPage()
	{
		return parseInt(this.el.dataset.page);
	}

	setPerPage(perPage)
	{
		this.el.dataset.perPage = perPage;

		return this;
	}

	getPerPage()
	{
		return parseInt(this.el.dataset.perPage);
	}

	hide()
	{
		this.el.hidden = true;

		return this;
	}

	show()
	{
		this.el.hidden = false;

		return this;
	}

	showNotEmpty()
	{
		this.getCount() ? this.show() : this.hide();

		return this;
	}

	getIsLoadingMore()
	{
		return !!(parseInt(this.el.dataset.isLoadingMore ?? 0));
	}

	setIsLoadingMore(value)
	{
		this.el.dataset.isLoadingMore = value ? 1 : 0;

		return this;
	}

	loadMoreIfApplicable()
	{
		const element = this.getLoadMoreElement();
		if (element) {
			const rect = element.getBoundingClientRect();

			if (
				rect.width &&
				rect.height &&
				rect.top >= 0 &&
				rect.left >= 0 &&
				(
					(rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)) ||
					(rect.bottom - (window.innerHeight || document.documentElement.clientHeight) < 200)
				) &&
				rect.right <= (window.innerWidth || document.documentElement.clientWidth)
			) {
				this.loadMore();
			}
		}
	}

	getRemainingCount()
	{
		const count = this.getTotal() - this.getCount();

		return (count >= 0) ? count : 0;
	}

	getMoreCountElement()
	{
		return this.el.querySelector(`${this.getSelector()}__more-count`);
	}

	getLoadMoreElement()
	{
		return this.el.querySelector(`${this.getSelector()}__load-more`);
	}

	hideLoadMore()
	{
		this.getLoadMoreElement().hidden = true;
	}

	showLoadMore()
	{
		this.getLoadMoreElement().hidden = !(this.getRemainingCount() > 0);
	}

	getNoDataElement()
	{
		return this.el.querySelector(`${this.getSelector()}__no-data`);
	}

	hideNoData()
	{
		this.getNoDataElement().hidden = true;

		return this;
	}

	showNoData()
	{
		this.getNoDataElement().hidden = false;

		return this;
	}

	getLimit()
	{
		return this.el.dataset?.limit ? parseInt(this.el.dataset?.limit) : null;
	}

	getEdibleContainer()
	{
		return new EdibleContainer(this.el.querySelector(EdibleContainer.selector()));
	}

	setFromResponse(response)
	{
		this.getEdibleContainer().set(response.data);

		return this;
	}

	setFromQueryResponse(response)
	{
		if (this.getCategory()) {
			const categoryResponse = response.data.categories[this.getCategory()];
			if (categoryResponse) {
				this.setPagination(categoryResponse.pagination);

				const edibles = this.getLimit() ? categoryResponse.items.slice(0, this.getLimit()) : categoryResponse.items;
				this.getEdibleContainer().set(edibles);

				categoryResponse.pagination.total ? this.hideNoData() : this.showNoData();
				categoryResponse.pagination.total ? this.getEdibleContainer().show() : this.getEdibleContainer().hide();
			}
		}

		return this;
	}

	getTagIds()
	{
		return this.el.dataset?.tagIds ?? "";
	}

	resetTags()
	{
		this.el.dataset.tagIds = "";
		this.getTagContainer()?.reset();

		return this;
	}

	setTags(tags)
	{
		const tagIds = Array.from(tags.getIds()).join(",");

		if (tagIds != this.el.dataset.tagIds) {
			this.el.dataset.tagIds = tagIds;

			this.getEdibleContainer().hide();
			this.hideLoadMore();
			this.showPreloader();

			console.log(this.getTagIds());
			console.log(isNull(this.getTagIds()));
			console.log(typeof this.getTagIds());

			BRequest.get(Form.getSearchURL(), {
				categories: this.getCategory(),
				page: 1,
				perPage: this.getPerPage(),
				query: this.getForm().getQueryElementValue(),
				tags: this.getTagIds(),
			}).then(response => {
				const categoryResponse = response.data.categories[this.getCategory()];

				this.setPagination(categoryResponse.pagination);

				this.getEdibleContainer().set(categoryResponse.items);
				this.getEdibleContainer().show();

				categoryResponse.pagination.total ? this.hideNoData() : this.showNoData();
				categoryResponse.pagination.total ? this.getEdibleContainer().show() : this.getEdibleContainer().hide();
			}).finally(() => {
				this.showLoadMore();
				this.hidePreloader();
			});
		}
	}

	loadMore()
	{
		if (this.getIsLoadingMore()) {
			return this;
		}
		if (!this.getRemainingCount()) {
			return this;
		}

		this.setIsLoadingMore(true);
		this.hideLoadMore();
		this.showPreloader();

		BRequest.get(Form.getSearchURL(), {
			categories: this.getCategory(),
			page: this.getPage() + 1,
			perPage: this.getPerPage(),
			query: this.getForm().getQuery(),
			tags: this.getTagIds(),
		}).then(response => {
			const categoryResponse = response.data.categories[this.getCategory()];

			this.setPagination(categoryResponse.pagination);

			this.getEdibleContainer().add(categoryResponse.items);
			this.getEdibleContainer().show();
		}).finally(() => {
			this.setIsLoadingMore(false);
			this.showLoadMore();
			this.hidePreloader();
		});
	}
}
